import { DropOverlay } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams, useSearchParams } from "react-router";
import { ApiError, listAgentsOptions } from "@/api";
import { storedShowContextInfo } from "@/features/account/storedSettings";
import { useCapabilities, useCurrentUser } from "@/features/account/useCurrentUser";
import { BuildEmptyState } from "@/features/build/withAi/BuildEmptyState";
import { useBuildChat } from "@/features/build/withAi/useBuildChat";
import { useSpaceHub } from "@/features/spaces/detail/useSpaceHub";
import { AskUserCard } from "./AskUserCard";
import { replyActionsFor } from "./actions/replyActionsContext";
import { ChatComposer } from "./ChatComposer";
import { useChatDocuments } from "./ChatDocumentsPanel";
import { ChatHeader } from "./ChatHeader";
import { visibleMessages } from "./ChatMessages";
import { ChatPreviewPanel } from "./ChatPreviewPanel";
import { ChatNotFound, ChatThread } from "./ChatThread";
import { type ChatHost, chatHost, chatScope, newChatScope, otherHostPath } from "./chatHost";
import { linkName, loadSharePointLink, takeNewSharePointLinks } from "./cloudFiles";
import { type DraftContextValue, DraftProvider } from "./drafts/DraftContext";
import { draftsFromMessages } from "./drafts/draftModel";
import { ForkBar } from "./ForkBar";
import { continueHandler } from "./PausedNotice";
import { ReplyFollowUps } from "./ReplyFollowUps";
import { answeringReasoningConfig, effectiveEffort } from "./reasoning";
import { type ProposalContextValue, ProposalProvider } from "./research/ProposalContext";
import { ShareChatDialog } from "./ShareChatDialog";
import { SourceFileProvider } from "./sourceFiles";
import { useAttachments } from "./useAttachments";
import { useChatActions } from "./useChatActions";
import { useChatAgent } from "./useChatAgent";
import { useChatBrand } from "./useChatBrand";
import { useChatFileEvents } from "./useChatFileEvents";
import { useChatModels } from "./useChatModels";
import { useChatEffort, useChatSession } from "./useChatSession";
import { chatStarted, useChatSetup } from "./useChatSetup";
import { useChatSwitchKey } from "./useChatSwitchKey";
import { useChatTurns } from "./useChatTurns";
import { useEnsureSession } from "./useEnsureSession";
import { useInitialPrefill, useInitialPrompt } from "./useInitialPrompt";
import { usePerChatState } from "./usePerChatState";
import { useRetryAfterSignIn } from "./useRetryAfterSignIn";

/**
 * `/chat` (new chat) and `/chat/:sessionUuid` (open chat). With the build host, the same page is Build with AI
 * (`/build/with-ai[/:sessionUuid]`): a build chat, on the default assistant, with build-focused copy.
 */
export function ChatPage({ host = chatHost }: Readonly<{ host?: ChatHost }>) {
  const { t } = useTranslation("chat");
  const isBuild = host.kind === "build";
  const { sessionUuid } = useParams();
  // Moving to another chat starts it fresh: nothing typed, attached or picked here follows (MHS-1039).
  const chatKey = useChatSwitchKey(sessionUuid);
  const [searchParams] = useSearchParams();
  const me = useCurrentUser();
  const capabilities = useCapabilities();
  const models = useChatModels();
  // Build chats always use the default assistant, so there is no agent to pick.
  const agents = useQuery({ ...listAgentsOptions(), select: (r) => r.data, enabled: !isBuild });
  const session = useChatSession(sessionUuid);
  const documents = useChatDocuments(sessionUuid);
  // Live file-state notices: a file's checks finishing refreshes its status and preview at once
  useChatFileEvents(sessionUuid, capabilities.chatFilePipelineEnabled);
  const { effort, setEffort } = useChatEffort(sessionUuid, session.data, chatKey);
  const build = useBuildChat(host, sessionUuid);
  const prefill = useInitialPrefill();

  const seed = build?.seed ?? prefill;
  const { draft, setDraft, pickedAgent, setPickedAgent, openDoc, setOpenDoc, shareOpen, setShareOpen, sourceFile } =
    usePerChatState(chatKey, { draft: seed, agent: searchParams.get("agent") });

  const messages = visibleMessages(session.data?.messages ?? []);
  const turns = useChatTurns({
    sessionUuid,
    chatKey,
    savedCount: messages.length,
    refetchSession: session.refetch,
    host,
    // The context-usage meter is opt-in (Settings › AI preferences).
    trackContext: storedShowContextInfo(me.data?.user.settings),
  });
  const started = chatStarted(sessionUuid, session.data, turns.busy);
  // `/chat?space=<uuid>` starts the new chat in that space.
  const { agentUuid, spaceUuid } = chatScope(
    sessionUuid,
    session.data,
    newChatScope(isBuild, pickedAgent, searchParams.get("space")),
  );
  const { agent, menuAgents } = useChatAgent({ agents: agents.data, agentUuid, isNewChat: !started });
  const setup = useChatSetup({ sessionUuid, agents: menuAgents, pickAgent: setPickedAgent, models });
  const brand = useChatBrand({ enabled: !isBuild, sessionUuid, started, session: session.data, setup, chatKey });
  const agentName = agent?.name ?? session.data?.agentName;
  const modelId = agent?.modelId ?? (sessionUuid ? session.data?.modelId : models.selectedId);
  const reasoning = answeringReasoningConfig(modelId, models.models, models.tierDefaults);
  const sendEffort = effectiveEffort(effort, reasoning);
  const sendModelId = agent?.modelId ?? models.selectedId;
  const savedDrafts = useMemo(() => draftsFromMessages(session.data?.messages ?? []), [session.data?.messages]);
  // People a chat was shared with read it; they fork it to continue.
  const isOwner = !session.data || !me.data || session.data.userUuid === me.data.user.uuid;
  // Documents in my chat in a space can be shared into it (editors of the space).
  const space = useSpaceHub(session.data?.spaceUuid ?? "", isOwner && Boolean(sessionUuid));
  const title = session.data?.title || build?.newChatTitle || t("header.newChat");

  const ensureSession = useEnsureSession(
    sessionUuid,
    { agentUuid, modelId: sendModelId, reasoningEffort: sendEffort, spaceUuid, brandUuid: brand.brandUuid },
    host,
    chatKey,
  );
  const attachments = useAttachments(ensureSession, chatKey);
  const actions = useChatActions({ sessionUuid, title, isOwner, onShare: () => setShareOpen(true), host });

  const sendText = (content: string, restore: () => void) => {
    // A pick being saved decides who answers: the message waits in the composer
    if (setup.saving) return restore();
    const { toSend, sent } = attachments;
    attachments.clear();
    turns.start(
      {
        content,
        agentUuid,
        modelId: sendModelId,
        spaceUuid,
        brandUuid: brand.brandUuid,
        // A pending level the model doesn't accept is dropped, not sent.
        reasoningEffort: sendEffort,
        ...toSend,
      },
      restore,
      sent,
    );
  };
  // Sends a message from elsewhere than the composer; refused, it lands in the composer to send again.
  const sendAgain = (text: string) => sendText(text, () => setDraft(text));
  const send = { agentUuid, modelId: sendModelId, reasoningEffort: sendEffort };
  const continueReply = continueHandler(isOwner, turns.start, send);
  useInitialPrompt(!sessionUuid, Boolean(agentUuid ? agent : models.selectedId), sendAgain);
  useRetryAfterSignIn(
    Boolean(sessionUuid) && !session.isPending && Boolean(agentUuid ? agent : models.selectedId),
    sendAgain,
  );
  // SharePoint/OneDrive links typed or pasted into the message become attachments.
  const changeDraft = (next: string) => {
    if (!capabilities.sharepointEnabled) return setDraft(next);
    const { text, urls } = takeNewSharePointLinks(draft, next);
    setDraft(text);
    attachments.addCloud(urls.map((url) => ({ name: linkName(url), load: () => loadSharePointLink(url) })));
  };
  const submit = () => {
    const content = draft.trim();
    if (!content || attachments.uploading) return;
    setDraft("");
    sendText(content, () => setDraft(content));
  };

  // 403 too: a chat I can't open looks the same as one that doesn't exist.
  if (session.error instanceof ApiError && (session.error.status === 404 || session.error.status === 403)) {
    return <ChatNotFound />;
  }
  // A chat opens in the host it belongs to (a build chat found under /chat, a plain chat under /build/with-ai).
  const elsewhere = otherHostPath(host, session.data);
  if (elsewhere) return <Navigate to={elsewhere} replace />;

  const docs = documents.data ?? [];
  const toggleDocuments = () => setOpenDoc((current) => (current ? null : { lineageUuid: docs[docs.length - 1].uuid }));
  const drafts: DraftContextValue = {
    sessionUuid,
    linked: session.data?.linkedEntities ?? [],
    saved: savedDrafts,
    streaming: turns.busy,
    readOnly: !isOwner,
    onAnswer: sendAgain,
  };
  // A proposal card decides with a message of its own: the composer's text and files stay where they are.
  const proposals: ProposalContextValue = {
    readOnly: !isOwner,
    busy: turns.busy,
    onDecide: (content, confirmation, onFailed) =>
      turns.start({ content, agentUuid, modelId: null, reasoningEffort: null, confirmation }, onFailed),
  };
  const chat = { sessionUuid, host, spaceUuid: session.data?.spaceUuid, isOwner, busy: turns.busy };
  const replyActions = replyActionsFor(chat, messages, turns.start, send);
  const page = (
    <DraftProvider value={drafts}>
      <div className="relative flex h-full min-h-0">
        <div className="flex min-w-0 flex-1 flex-col">
          <ChatHeader
            title={title}
            items={actions.items}
            spaceUuid={spaceUuid}
            history={{ label: build?.rootLabel, to: host.historyPath }}
            documentsOpen={openDoc !== null}
            onToggleDocuments={docs.length > 0 ? toggleDocuments : undefined}
          />
          <ChatThread
            loading={Boolean(sessionUuid) && session.isPending}
            messages={messages}
            myUuid={me.data?.user.uuid}
            agentName={agentName}
            documents={docs}
            onOpenDocument={setOpenDoc}
            pending={turns.pending}
            optimisticPrompt={turns.optimisticPrompt}
            optimisticAttachments={turns.optimisticAttachments}
            optimisticContinuation={turns.optimisticContinuation}
            continueDisabled={turns.busy || actions.forking}
            onSettled={turns.settle}
            onAskUser={turns.setAskUser}
            onExtensionAccess={turns.onExtensionAccess}
            onConnectorConnect={turns.onConnectorConnect}
            onContext={turns.onContext}
            drafts={savedDrafts}
            replyActions={replyActions}
            onContinue={isOwner ? continueReply : actions.fork}
            emptyState={
              build ? (
                <BuildEmptyState
                  entity={build.entity}
                  // A chat Edit with AI opened: nothing said yet, the entity it was opened for is linked.
                  editing={session.data?.linkedEntities?.[0]?.name}
                  onSend={(text) => {
                    // The example replaces the prefilled sentence; text the user typed stays.
                    setDraft((current) => (current === seed ? "" : current));
                    sendAgain(text);
                  }}
                />
              ) : undefined
            }
            after={
              turns.askUser && <AskUserCard key={turns.pending?.opId} prompt={turns.askUser} onSubmit={sendAgain} />
            }
          />
          {isOwner ? (
            <ChatComposer
              value={draft}
              onChange={changeDraft}
              onSend={submit}
              busy={turns.busy || setup.saving}
              generating={turns.generating}
              onStop={turns.stop}
              started={started}
              chatKey={chatKey}
              agents={menuAgents}
              hideAssistant={isBuild}
              placeholder={build?.placeholder}
              agentUuid={agentUuid}
              agentName={agentName}
              commands={agent?.slashCommands}
              onAgentChange={setup.changeAgent}
              modelOptions={models.options}
              modelId={modelId ?? null}
              modelLabel={models.options.find((o) => o.id === modelId)?.label ?? modelId ?? ""}
              onModelChange={setup.changeModel}
              reasoning={reasoning}
              effort={sendEffort}
              onEffortChange={setEffort}
              noModel={!sessionUuid && !agentUuid && models.noModel}
              attachments={attachments.composerItems}
              attachmentNotices={attachments.checkNotices}
              uploading={attachments.uploading}
              onAttach={(files) => void attachments.add(files)}
              accept={attachments.accept}
              onAttachCloud={attachments.addCloud}
              folderLink={attachments.folderLink}
              onRemoveAttachment={attachments.remove}
              context={turns.context}
              brand={brand.picker}
            />
          ) : (
            <ForkBar owner={session.data?.userName} forking={actions.forking} onFork={actions.fork} />
          )}
        </div>
        <ChatPreviewPanel documents={docs} doc={openDoc} onOpenDoc={setOpenDoc} file={sourceFile} space={space.data} />
      </div>
      {actions.dialog}
      <ReplyFollowUps turns={turns} retry={sendAgain} />
      {sessionUuid && isOwner && (
        <ShareChatDialog sessionUuid={sessionUuid} open={shareOpen} onClose={() => setShareOpen(false)} />
      )}
    </DraftProvider>
  );
  const withCards = (
    <ProposalProvider value={proposals}>
      <SourceFileProvider value={sourceFile.open}>{page}</SourceFileProvider>
    </ProposalProvider>
  );
  // Only the owner can post in the chat, so only they can drop files on it.
  if (!isOwner) return withCards;
  return (
    <DropOverlay onFilesDropped={(files) => void attachments.add(files)} message={t("attachments.drop")}>
      {withCards}
    </DropOverlay>
  );
}
